import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TarifFretDto, TypeTarifFret, TypeFretDto, UpdateTarifFretListDto,  IlesLexiClient, TypesFretLexiClient, IleDto, TarifsFretLexiClient } from '@lexi-clients/lexi';
import { DxDataGridComponent, DxFormComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { NotificationType } from 'lexi-angular/src/app/modules/shared/references/references';
import { forkJoin, lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-article-tarifs-fret',
    templateUrl: './article-tarifs-fret.component.html',
    styleUrls: ['./article-tarifs-fret.component.scss'],
    standalone: false
})
export class ArticleTarifsFretComponent implements OnInit {
  @ViewChild("dataGrid") dataGrid: DxDataGridComponent;
  @ViewChild("form") popupForm: DxFormComponent;
  @Input() disabled = false;

  private _articleId: number;
  get articleId() { return this._articleId; }
  @Input() set articleId(value: number) {
    this._articleId = value;
    this.setDataSource();
  }

  private _tarifRevatuaId: number;
  get tarifRevatuaId() { return this._tarifRevatuaId; }
  @Input() set tarifRevatuaId(value: number) {
    this._tarifRevatuaId = value;
  }

  tarifsFret: TarifFretDto[] = [];
  grilleStorageKey = "grille_tarifs_fret_article";
  typesTarifsFret: Array<{ id: TypeTarifFret, intitule: string }> = [
    { id: TypeTarifFret.poidsVolume, intitule: "Poids Volume" },
    { id: TypeTarifFret.poids, intitule: "Poids" },
    { id: TypeTarifFret.volume, intitule: "Volume" },
    { id: TypeTarifFret.quantite, intitule: "Quantité" },
  ];
  typesFret: TypeFretDto[] = [];
  dataSourceIles: IleDto[] = [];
  showPopup = false;
  formData: { montant: number, typeTarifFret: TypeTarifFret } = { montant: 0, typeTarifFret: TypeTarifFret.quantite };
  selectedRows: TypeFretDto[] = [];

  constructor(
    private readonly ilesLexiClient : IlesLexiClient,
    private readonly typesFretLexiClient : TypesFretLexiClient,
    private readonly tarifsFretLexiClient: TarifsFretLexiClient
  ) { }

  ngOnInit() {
    this.loadDataGrid();
  }

  async setDataSource() {
    this.tarifsFret = await lastValueFrom(this.tarifsFretLexiClient.getByArticleId(this.articleId));
  }

  async loadDataGrid() {
    // Récupération des dataSources
    const dataSources = await lastValueFrom(forkJoin({
      iles: this.ilesLexiClient.getAll(),
      typesFret: this.typesFretLexiClient.getAll()
    }))

    // Alimentation des datasources locales pour ne pas refaire d'appel API
    this.dataSourceIles = dataSources.iles;
    this.typesFret = dataSources.typesFret;
  }

  async onRowUpdating(e: any) {
    try {
      const tarifFret: TarifFretDto = {...e.oldData, ...e.newData}
      await lastValueFrom(this.tarifsFretLexiClient.update(tarifFret))
      notify({closeOnClick: true, message: "Modifications effectuées avec succès"}, NotificationType.Success);
    }
    finally {
      await this.setDataSource();
    }
  }

  async onRowInserting(newTarifFret: TarifFretDto) {
    try {
      newTarifFret.typeFretId = this.typesFret.find(x => x.tarifRevatuaId == this.tarifRevatuaId)?.id;
      newTarifFret.articleId = this.articleId;
      await lastValueFrom(this.tarifsFretLexiClient.create(newTarifFret));
      notify({closeOnClick: true, message: "Insertion effectuée avec succès"}, NotificationType.Success);
    }
    finally {
      await this.setDataSource();
    }
  }

  async onRowRemoving(tarifFretToDelete: TarifFretDto) {
    try {
      await lastValueFrom(this.tarifsFretLexiClient._delete(tarifFretToDelete.id));
      notify({closeOnClick: true, message: "Suppression effectuée avec succès"}, NotificationType.Success);
    }
    finally {
      await this.setDataSource();
    }
  }

  onSelectionChanged(e: SelectionChangedEvent) {
    this.selectedRows = e.selectedRowsData;
  }

  onOpenPopup = () => {
    const first: TarifFretDto = this.selectedRows[0];
    this.formData = { montant: 0, typeTarifFret: first.typeTarifFret };
    this.showPopup = true;
  }

  onCancelPopup = () => {
    this.showPopup = false;
    this.formData = { montant: 0, typeTarifFret: TypeTarifFret.quantite };
  }

  onSavePopup = async () => {
    try {
      const selectedRows: TarifFretDto[] = this.selectedRows;
      if (selectedRows?.length > 0) {
        const dto: UpdateTarifFretListDto = {
          tarifsFret: selectedRows,
          montant: this.formData.montant,
          typeTarifFret: this.formData.typeTarifFret
        }
        await lastValueFrom(this.tarifsFretLexiClient.updateList(dto));
        this.showPopup = false;
        this.formData = { montant: 0, typeTarifFret: TypeTarifFret.quantite };
      }
    }
    finally {
      await this.setDataSource();
    }
  }

  async reinitialiserTarifsFret() {
    if (this.articleId == null) {
      notify({closeOnClick: true, message: "L'article n'est pas renseigné, réessayez après avoir recharger la page." }, NotificationType.Warning);
      return;
    }

    const initialiser = this.tarifsFret?.length > 0 ? `réinitialiser` : `créer`;
    const confirmed = await confirm(`Souhaitez-vous ${initialiser} les tarifs fret personnalisés de l'article en vous basant sur le code tarif Revatua ?`, `Confirmation`);
    if (!confirmed) return;

    try {
      await lastValueFrom(this.tarifsFretLexiClient.reinitialiser(this.articleId));
    }
    finally {
      await this.setDataSource();
    }
  }

  getTexteLignesSelectionees() {
    const selectedRows = this.selectedRows;
    const s = selectedRows?.length > 1 ? 's' : '';
    return `${selectedRows?.length ?? 0} ligne${s} sélectionnée${s}`
  }

}
